import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  Upload,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API_URL } from "@/lib/api";

const API = API_URL;

const FORMAT_LABELS = {
  auto: "Auto-detect",
  mt4: "MT4 / MT5 statement",
  ctrader: "cTrader",
  generic: "Generic CSV",
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

function formatSigned(value, digits = 2) {
  const numeric = Number(value);
  return `${numeric > 0 ? "+" : ""}${numeric.toFixed(digits)}`;
}

export default function ImportView({ currentStrategy, mode }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState("auto");
  const [defaultRisk, setDefaultRisk] = useState("0");
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const runPreview = useCallback(
    async (selectedFile = file, overrides = {}) => {
      if (!selectedFile) {
        return;
      }
      setLoading(true);
      setSummary(null);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("format", overrides.format ?? format);
        formData.append("default_risk_amount", (overrides.defaultRisk ?? defaultRisk) || "0");
        const response = await axios.post(
          `${API}/import/preview?mode=${mode}`,
          formData,
          { headers: authHeaders() }
        );
        setPreview(response.data);
      } catch (error) {
        setPreview(null);
        toast.error(error.response?.data?.detail || "Failed to read the CSV file");
      } finally {
        setLoading(false);
      }
    },
    [defaultRisk, file, format, mode]
  );

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setPreview(null);
    setSummary(null);
    if (selectedFile) {
      runPreview(selectedFile);
    }
  };

  const handleFormatChange = (value) => {
    setFormat(value);
    if (file) {
      runPreview(file, { format: value });
    }
  };

  const handleImport = async () => {
    if (!file || !preview || !currentStrategy) {
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("format", format);
      formData.append("default_risk_amount", defaultRisk || "0");
      formData.append("strategy_id", currentStrategy.id);
      formData.append("dedupe", "true");
      const response = await axios.post(
        `${API}/import/trades?mode=${mode}`,
        formData,
        { headers: authHeaders() }
      );
      setSummary(response.data);
      setPreview(null);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success(`Imported ${response.data.imported} trades`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const resetAll = () => {
    setFile(null);
    setPreview(null);
    setSummary(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (!currentStrategy) {
    return (
      <div className="p-4 md:p-6" data-testid="import-no-strategy">
        <EmptyState
          icon={Upload}
          title="No strategy selected"
          description="Create or select a strategy before importing trades from your broker."
        />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="import-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold md:text-3xl">
          <Upload className="h-7 w-7 text-primary" />
          Import trades
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Import a broker CSV export into <span className="font-medium">{currentStrategy.name}</span>.
          Nothing is written until you confirm the preview.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="shadow-sm lg:col-span-1">
          <CardHeader className="pb-4">
            <CardTitle className="text-base">1 · Choose the file</CardTitle>
            <CardDescription>CSV export from MT4/MT5, cTrader or any statement with date, symbol and P&L.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="import-file">CSV file</Label>
              <Input
                id="import-file"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={handleFileChange}
                data-testid="import-file-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="import-format">Format</Label>
              <Select value={format} onValueChange={handleFormatChange}>
                <SelectTrigger id="import-format" data-testid="import-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FORMAT_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="import-risk">Default risk per trade ({currentStrategy.currency})</Label>
              <Input
                id="import-risk"
                type="number"
                min="0"
                step="0.01"
                value={defaultRisk}
                onChange={(event) => setDefaultRisk(event.target.value)}
                onBlur={() => file && runPreview(file)}
                data-testid="import-risk-input"
              />
              <p className="text-xs text-muted-foreground">
                Used when the CSV has no risk column; keeps R-metrics meaningful. Set 0 to leave it empty.
              </p>
            </div>
            {file && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => runPreview(file)}
                disabled={loading}
                data-testid="import-refresh-preview"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refresh preview
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm lg:col-span-2">
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">2 · Review the preview</CardTitle>
                <CardDescription>
                  {preview
                    ? `${preview.valid_rows} importable rows · ${preview.errors.length} with errors`
                    : "Upload a file to see the parsed trades."}
                </CardDescription>
              </div>
              {preview && (
                <Badge variant="outline" className="border-primary/40 bg-primary/15 text-primary">
                  {FORMAT_LABELS[preview.format] || preview.format}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="import-loading">
                Reading the file…
              </p>
            )}

            {!loading && !preview && !summary && (
              <div className="flex flex-col items-center justify-center py-10 text-center text-sm text-muted-foreground">
                <FileSpreadsheet className="mb-3 h-10 w-10 text-muted-foreground/40" />
                No file parsed yet.
              </div>
            )}

            {preview && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {preview.errors.length > 0 && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-400"
                    data-testid="import-errors"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="space-y-0.5">
                      {preview.errors.slice(0, 5).map((error) => (
                        <p key={`${error.row}-${error.error}`}>
                          Row {error.row}: {error.error}
                        </p>
                      ))}
                      {preview.errors.length > 5 && <p>…and {preview.errors.length - 5} more.</p>}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Date</th>
                        <th className="py-2 pr-3 font-medium">Pair</th>
                        <th className="py-2 pr-3 font-medium">P&L</th>
                        <th className="py-2 pr-3 font-medium">Risk</th>
                        <th className="py-2 font-medium">Ticket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row) => (
                        <tr key={`${row.external_id || "row"}-${row.execution_date}-${row.currency_pair}-${row.profit_loss}`} className="border-b border-border/60 last:border-0">
                          <td className="py-2 pr-3 text-muted-foreground">{row.execution_date}</td>
                          <td className="py-2 pr-3 font-medium">{row.currency_pair}</td>
                          <td
                            className={`py-2 pr-3 font-medium ${
                              row.profit_loss >= 0 ? "text-success" : "text-destructive"
                            }`}
                          >
                            {formatSigned(row.profit_loss)} {currentStrategy.currency}
                          </td>
                          <td className="py-2 pr-3 text-muted-foreground">
                            {row.risk_amount > 0 ? row.risk_amount.toFixed(2) : "—"}
                          </td>
                          <td className="py-2 text-muted-foreground">{row.external_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.valid_rows > preview.rows.length && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Showing the first {preview.rows.length} of {preview.valid_rows} rows.
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleImport} disabled={importing} data-testid="import-confirm">
                    <Upload className="mr-2 h-4 w-4" />
                    {importing ? "Importing…" : `Import ${preview.valid_rows} trades`}
                  </Button>
                </div>
              </motion.div>
            )}

            {summary && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
                data-testid="import-summary"
              >
                <div className="flex items-center gap-3 rounded-lg border border-success/40 bg-success/10 px-4 py-3 text-success">
                  <CheckCircle2 className="h-5 w-5" />
                  <div>
                    <p className="text-sm font-semibold">
                      Imported {summary.imported} trades into {currentStrategy.name}
                    </p>
                    <p className="text-xs opacity-80">
                      {summary.skipped_duplicates} duplicates skipped · {summary.errors.length} rows with errors ·
                      format {FORMAT_LABELS[summary.format] || summary.format}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={resetAll} data-testid="import-again">
                    Import another file
                  </Button>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
